import { dmFetch } from "./client";
import type { AccHub } from "./types";

interface WorkspaceHubAttributes {
  hubUrn?: unknown;
  sourceId?: unknown;
  hubName?: unknown;
  sourceRegion?: unknown;
  source?: unknown;
}

interface WorkspaceHubEntry {
  id?: unknown;
  attributes?: WorkspaceHubAttributes;
}

interface WorkspaceHubsResponse {
  data?: WorkspaceHubEntry[];
}

/** Forge DM hub id from Common Workspace `sourceId` (ACC account UUID). */
export function dmHubIdFromSourceId(sourceId: string): string {
  return sourceId.startsWith("b.") ? sourceId : `b.${sourceId}`;
}

function mapWorkspaceHub(entry: WorkspaceHubEntry): AccHub | null {
  const attrs = entry.attributes ?? {};
  const sourceId =
    typeof attrs.sourceId === "string" ? attrs.sourceId.trim() : "";
  if (!sourceId) return null;

  const industry = typeof attrs.source === "string" ? attrs.source : "";
  // ACC / BIM 360 / Forma Docs hubs report `bim`. Fusion uses other values.
  if (industry && industry !== "bim") return null;

  const region =
    typeof attrs.sourceRegion === "string" ? attrs.sourceRegion : undefined;
  const name =
    typeof attrs.hubName === "string" && attrs.hubName.length > 0
      ? attrs.hubName
      : "Hub";

  return {
    id: dmHubIdFromSourceId(sourceId),
    name,
    kind: "acc",
    extensionType: "workspace:common",
    region,
  };
}

/**
 * Fallback hub discovery when `GET /project/v1/hubs` returns empty.
 * Common Workspace lists hubs the signed-in user can access even when DM
 * provisioning is still catching up.
 */
export async function fetchWorkspaceAccHubs(
  accessToken: string
): Promise<AccHub[]> {
  const json = await dmFetch<WorkspaceHubsResponse>(
    accessToken,
    "/workspace/v1/hubs"
  );
  const hubs: AccHub[] = [];
  for (const entry of json.data ?? []) {
    const hub = mapWorkspaceHub(entry);
    if (hub) hubs.push(hub);
  }
  return hubs;
}

export function mergeAccHubs(primary: AccHub[], secondary: AccHub[]): AccHub[] {
  const byId = new Map<string, AccHub>();
  for (const hub of [...primary, ...secondary]) {
    if (!byId.has(hub.id)) byId.set(hub.id, hub);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
